import { PRODUCT_MAPPING_STATUSES, type ProductMappingStatus } from "./statuses.js";

export const MONITORING_CADENCES = ["DAILY", "TWICE_DAILY", "WEEKLY"] as const;
export type MonitoringCadence = (typeof MONITORING_CADENCES)[number];

export type MonitoringChangeType =
  | "PRICE_CHANGED"
  | "AVAILABILITY_CHANGED"
  | "CURRENCY_CHANGED"
  | "SELLER_CHANGED"
  | "IDENTITY_CONFIDENCE_CHANGED"
  | "EXTRACTION_HEALTH_CHANGED";

export const MAPPING_MONITORING_STATUSES = PRODUCT_MAPPING_STATUSES;
export type MappingMonitoringStatus = ProductMappingStatus;

export interface MonitoringPlanLimits {
  readonly maxMonitoredMappings: number;
  readonly allowedCadences: readonly MonitoringCadence[];
}

export interface MonitoringMappingSnapshot {
  readonly mappingId: string;
  readonly status: MappingMonitoringStatus;
  readonly monitoringEnabled: boolean;
}

export interface MonitoringScheduleRequest {
  readonly enabled: boolean;
  readonly cadence: MonitoringCadence;
  readonly timezone: string;
  readonly fromUtc: string;
  readonly priority?: number;
  readonly plan: MonitoringPlanLimits;
  readonly currentEnabledCount: number;
  readonly mapping: MonitoringMappingSnapshot;
}

export interface MonitoringScheduleDecision {
  readonly monitoringEnabled: boolean;
  readonly cadence: MonitoringCadence;
  readonly timezone: string;
  readonly nextCheckAt: string | null;
  readonly freshnessTtlMinutes: number;
  readonly priority: number;
}

export interface MonitoringObservationSnapshot {
  readonly priceMinor: number | null;
  readonly currency: string | null;
  readonly availability: string | null;
  readonly sellerName?: string | null;
  readonly identityConfidence?: number | null;
  readonly extractionConfidence?: number | null;
  readonly extractionSucceeded?: boolean | null;
  readonly observedAt: string;
}

export interface MonitoringChangeEvent {
  readonly type: MonitoringChangeType;
  readonly previousValue: string | number | boolean | null;
  readonly currentValue: string | number | boolean | null;
  readonly observedAt: string;
}

const CADENCE_MINUTES: Record<MonitoringCadence, number> = {
  DAILY: 24 * 60,
  TWICE_DAILY: 12 * 60,
  WEEKLY: 7 * 24 * 60,
};

const DEFAULT_PRIORITY = 50;

export function resolveMonitoringSchedule(
  request: MonitoringScheduleRequest,
): MonitoringScheduleDecision {
  ensureTimezoneIsValid(request.timezone);
  ensureCadenceIsAllowed(request.cadence, request.plan);

  if (!request.enabled) {
    return disabledSchedule(request);
  }

  ensureMappingCanBeMonitored(request.mapping);
  ensurePlanHasCapacity(request);

  return enabledSchedule(request);
}

export function calculateNextMonitoringCheckAt(
  fromUtc: string,
  cadence: MonitoringCadence,
  timezone: string,
): string {
  ensureTimezoneIsValid(timezone);
  const anchor = parseUtcInstant(fromUtc);
  return new Date(anchor.getTime() + CADENCE_MINUTES[cadence] * 60_000).toISOString();
}

export function calculateMonitoringFreshnessTtlMinutes(cadence: MonitoringCadence): number {
  return CADENCE_MINUTES[cadence] * 2;
}

export function detectMonitoringChanges(
  previous: MonitoringObservationSnapshot | null,
  current: MonitoringObservationSnapshot,
  priceToleranceMinor = 0,
): MonitoringChangeEvent[] {
  if (previous === null) {
    return [];
  }

  return [
    detectCurrencyChange(previous, current),
    detectPriceChange(previous, current, priceToleranceMinor),
    detectAvailabilityChange(previous, current),
    detectSellerChange(previous, current),
    detectIdentityConfidenceChange(previous, current),
    detectExtractionHealthChange(previous, current),
  ].filter(isMonitoringChangeEvent);
}

export function isMonitoringObservationStale(
  observedAt: string,
  nowUtc: string,
  cadence: MonitoringCadence,
): boolean {
  const observed = parseUtcInstant(observedAt).getTime();
  const now = parseUtcInstant(nowUtc).getTime();
  return now - observed > calculateMonitoringFreshnessTtlMinutes(cadence) * 60_000;
}

function enabledSchedule(request: MonitoringScheduleRequest): MonitoringScheduleDecision {
  return {
    monitoringEnabled: true,
    cadence: request.cadence,
    timezone: request.timezone,
    nextCheckAt: calculateNextMonitoringCheckAt(request.fromUtc, request.cadence, request.timezone),
    freshnessTtlMinutes: calculateMonitoringFreshnessTtlMinutes(request.cadence),
    priority: request.priority ?? DEFAULT_PRIORITY,
  };
}

function disabledSchedule(request: MonitoringScheduleRequest): MonitoringScheduleDecision {
  return {
    monitoringEnabled: false,
    cadence: request.cadence,
    timezone: request.timezone,
    nextCheckAt: null,
    freshnessTtlMinutes: calculateMonitoringFreshnessTtlMinutes(request.cadence),
    priority: request.priority ?? DEFAULT_PRIORITY,
  };
}

function ensureMappingCanBeMonitored(mapping: MonitoringMappingSnapshot): void {
  if (mapping.status !== "VERIFIED") {
    throw new Error("Only verified competitor mappings can be monitored.");
  }
}

function ensurePlanHasCapacity(request: MonitoringScheduleRequest): void {
  const enablingNewMapping = !request.mapping.monitoringEnabled;
  const nextEnabledCount = request.currentEnabledCount + (enablingNewMapping ? 1 : 0);

  if (nextEnabledCount > request.plan.maxMonitoredMappings) {
    throw new Error("Monitoring plan limit exceeded.");
  }
}

function ensureCadenceIsAllowed(cadence: MonitoringCadence, plan: MonitoringPlanLimits): void {
  if (!plan.allowedCadences.includes(cadence)) {
    throw new Error("Monitoring cadence is not available on the current plan.");
  }
}

function ensureTimezoneIsValid(timezone: string): void {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
  } catch {
    throw new Error("Monitoring timezone is invalid.");
  }
}

function parseUtcInstant(value: string): Date {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Expected a valid UTC instant.");
  }
  return parsed;
}

function detectCurrencyChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
): MonitoringChangeEvent | null {
  if (previous.currency === current.currency) {
    return null;
  }

  return toChangeEvent("CURRENCY_CHANGED", previous.currency, current.currency, current.observedAt);
}

function detectPriceChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
  priceToleranceMinor: number,
): MonitoringChangeEvent | null {
  if (previous.priceMinor === null || current.priceMinor === null) {
    return null;
  }

  if (Math.abs(current.priceMinor - previous.priceMinor) <= priceToleranceMinor) {
    return null;
  }

  return toChangeEvent(
    "PRICE_CHANGED",
    previous.priceMinor,
    current.priceMinor,
    current.observedAt,
  );
}

function detectAvailabilityChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
): MonitoringChangeEvent | null {
  if (previous.availability === current.availability) {
    return null;
  }

  return toChangeEvent(
    "AVAILABILITY_CHANGED",
    previous.availability,
    current.availability,
    current.observedAt,
  );
}

function detectSellerChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
): MonitoringChangeEvent | null {
  if ((previous.sellerName ?? null) === (current.sellerName ?? null)) {
    return null;
  }

  return toChangeEvent(
    "SELLER_CHANGED",
    previous.sellerName ?? null,
    current.sellerName ?? null,
    current.observedAt,
  );
}

function detectIdentityConfidenceChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
): MonitoringChangeEvent | null {
  if ((previous.identityConfidence ?? null) === (current.identityConfidence ?? null)) {
    return null;
  }

  return toChangeEvent(
    "IDENTITY_CONFIDENCE_CHANGED",
    previous.identityConfidence ?? null,
    current.identityConfidence ?? null,
    current.observedAt,
  );
}

function detectExtractionHealthChange(
  previous: MonitoringObservationSnapshot,
  current: MonitoringObservationSnapshot,
): MonitoringChangeEvent | null {
  if ((previous.extractionSucceeded ?? null) === (current.extractionSucceeded ?? null)) {
    return null;
  }

  return toChangeEvent(
    "EXTRACTION_HEALTH_CHANGED",
    previous.extractionSucceeded ?? null,
    current.extractionSucceeded ?? null,
    current.observedAt,
  );
}

function toChangeEvent(
  type: MonitoringChangeType,
  previousValue: string | number | boolean | null,
  currentValue: string | number | boolean | null,
  observedAt: string,
): MonitoringChangeEvent {
  return { type, previousValue, currentValue, observedAt };
}

function isMonitoringChangeEvent(
  value: MonitoringChangeEvent | null,
): value is MonitoringChangeEvent {
  return value !== null;
}
